/**
 * Utility functions for language preference storage
 */

/**
 * Interface for language preference stored in localStorage
 */
export interface LanguagePreference {
  language: 'en' | 'fr';
  isUserChoice: boolean;
  timestamp: number;
}

// Key used for storing language preference in localStorage
const LANGUAGE_PREFERENCE_KEY = 'language_preference';

/**
 * Gets the stored language preference
 * @returns The stored language preference or null if not found
 */
export function getLanguagePreference(): LanguagePreference | null {
  try {
    const storedPreference = localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
    if (!storedPreference) return null;

    const preference = JSON.parse(storedPreference) as LanguagePreference;

    // Validate the structure
    if (
      typeof preference === 'object' &&
      (preference.language === 'en' || preference.language === 'fr') &&
      typeof preference.isUserChoice === 'boolean' &&
      typeof preference.timestamp === 'number'
    ) {
      return preference;
    }

    return null;
  } catch (error) {
    console.error('Error reading language preference:', error);
    return null;
  }
}

/**
 * Sets the language preference in localStorage
 * @param language The language code ('en' or 'fr')
 * @param isUserChoice Whether this was explicitly chosen by the user
 */
export function setLanguagePreference(
  language: 'en' | 'fr',
  isUserChoice: boolean
): void {
  try {
    const preference: LanguagePreference = {
      language,
      isUserChoice,
      timestamp: Date.now(),
    };

    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, JSON.stringify(preference));
  } catch (error) {
    console.error('Error saving language preference:', error);
  }
}